import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { readSitePreview } from 'helpers/sitePreview';
import {
	currentStoreId,
	hitHomeClick,
	keyFromElement,
	keyFromEvent,
	subscribeHomeClicks,
} from 'helpers/homeClicks';

const SELECTOR =
	'a[href], button, [role="menuitem"], [role="button"], [data-click-key], .quality-card';

const HomeClickTracker = () => {
	const location = useLocation();
	const isHome = location.pathname === '/';
	const isStats = location.pathname === '/stats';
	const [preview] = useState(readSitePreview);
	const [counts, setCounts] = useState({ 0: {}, 1: {} });
	const city = currentStoreId();
	const [badges, setBadges] = useState([]);
	const sigRef = useRef('');

	useEffect(() => {
		if (isStats) return undefined;
		const onClick = event => {
			if (event.button !== 0) return;
			if (event.target.closest && event.target.closest('[data-click-ignore]')) return;
			const key = keyFromEvent(event);
			if (!key) return;
			hitHomeClick(key);
		};
		document.addEventListener('click', onClick, true);
		return () => document.removeEventListener('click', onClick, true);
	}, [isStats]);

	useEffect(() => {
		if (!isHome || !preview) return undefined;
		return subscribeHomeClicks(setCounts);
	}, [isHome, preview]);

	useEffect(() => {
		if (!isHome || !preview) {
			setBadges([]);
			return undefined;
		}
		let frame = 0;
		const scan = () => {
			const next = [];
			let n = 0;
			document.querySelectorAll(SELECTOR).forEach(node => {
				if (node.closest('.home-click-layer, .slick-cloned')) return;
				const key = keyFromElement(node);
				if (!key) return;
				const rect = node.getBoundingClientRect();
				if (rect.width < 8 || rect.height < 8) return;
				if (rect.bottom < 0 || rect.top > window.innerHeight) return;
				if (rect.right < 0 || rect.left > window.innerWidth) return;
				const big = rect.width > 100 && rect.height > 48;
				next.push({
					id: `${n}:${key}`,
					key,
					top: big ? rect.top + 8 : rect.top,
					left: big ? rect.right - 8 : rect.right,
					big,
				});
				n += 1;
			});
			const sig = next
				.map(b => `${b.id}@${Math.round(b.top)}:${Math.round(b.left)}`)
				.join('|');
			if (sig === sigRef.current) return;
			sigRef.current = sig;
			setBadges(next);
		};
		const schedule = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(scan);
		};
		schedule();
		window.addEventListener('scroll', schedule, true);
		window.addEventListener('resize', schedule);
		const obs = new MutationObserver(schedule);
		obs.observe(document.body, { childList: true, subtree: true });
		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener('scroll', schedule, true);
			window.removeEventListener('resize', schedule);
			obs.disconnect();
			sigRef.current = '';
		};
	}, [isHome, preview]);

	if (!isHome || !preview || typeof document === 'undefined') return null;

	return createPortal(
		<div className='home-click-layer'>
			{badges.map(badge => (
				<span
					key={badge.id}
					className={`home-click-stat home-click-stat--float${
						badge.big ? ' home-click-stat--float-in' : ''
					}`}
					style={{ top: badge.top, left: badge.left }}
				>
					{(Number(counts[city]?.[badge.key]) || 0).toLocaleString('ru-RU')}
				</span>
			))}
		</div>,
		document.body
	);
};

export default HomeClickTracker;
